import { useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import ImageExt from "@tiptap/extension-image";
import { Bold, Italic, Strikethrough, Heading2, Heading3, List, ListOrdered, Quote, Link2, Link2Off, ImagePlus, Undo2, Redo2 } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { uploadSiteImage } from "./fields";

function ToolBtn({ active, disabled, onClick, title, testid, children }) {
  return (
    <button type="button" title={title} aria-label={title} onClick={onClick} disabled={disabled} data-testid={testid}
      className={`h-9 w-9 grid place-items-center rounded-lg border transition-colors disabled:opacity-40 ${
        active ? "bg-[#005EB8] border-[#005EB8] text-white" : "border-slate-300 text-slate-600 hover:border-[#005EB8] hover:text-[#005EB8] bg-white"}`}>
      {children}
    </button>
  );
}

export function RichEditor({ value, onChange, testid }) {
  const [imgBusy, setImgBusy] = useState(false);
  const editor = useEditor({
    extensions: [StarterKit.configure({ link: { openOnClick: false } }), ImageExt],
    content: value || "",
    editorProps: { attributes: { class: "blog-edit outline-none min-h-[220px]" } },
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
  });
  if (!editor) return null;

  const addLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link address (https://…)", prev);
    if (url === null) return;
    if (!url.trim()) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
  };

  const addImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImgBusy(true);
    try {
      const url = await uploadSiteImage(f);
      editor.chain().focus().setImage({ src: url }).run();
      toast.success("Image added to the post");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setImgBusy(false);
    }
  };

  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap items-center gap-1.5 mb-2">
        <ToolBtn title="Bold" testid={`${testid}-bold`} active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="h-4 w-4" /></ToolBtn>
        <ToolBtn title="Italic" testid={`${testid}-italic`} active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="h-4 w-4" /></ToolBtn>
        <ToolBtn title="Strikethrough" testid={`${testid}-strike`} active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()}><Strikethrough className="h-4 w-4" /></ToolBtn>
        <span className="w-px h-6 bg-slate-200 mx-1" />
        <ToolBtn title="Heading" testid={`${testid}-h2`} active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 className="h-4 w-4" /></ToolBtn>
        <ToolBtn title="Sub-heading" testid={`${testid}-h3`} active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 className="h-4 w-4" /></ToolBtn>
        <span className="w-px h-6 bg-slate-200 mx-1" />
        <ToolBtn title="Bullet list" testid={`${testid}-ul`} active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="h-4 w-4" /></ToolBtn>
        <ToolBtn title="Numbered list" testid={`${testid}-ol`} active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="h-4 w-4" /></ToolBtn>
        <ToolBtn title="Quote" testid={`${testid}-quote`} active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote className="h-4 w-4" /></ToolBtn>
        <span className="w-px h-6 bg-slate-200 mx-1" />
        <ToolBtn title="Add link" testid={`${testid}-link`} active={editor.isActive("link")} onClick={addLink}><Link2 className="h-4 w-4" /></ToolBtn>
        {editor.isActive("link") && (
          <ToolBtn title="Remove link" testid={`${testid}-unlink`} onClick={() => editor.chain().focus().unsetLink().run()}><Link2Off className="h-4 w-4" /></ToolBtn>
        )}
        <label title="Insert image" className={`h-9 w-9 grid place-items-center rounded-lg border transition-colors cursor-pointer ${imgBusy ? "opacity-40" : "border-slate-300 text-slate-600 hover:border-[#005EB8] hover:text-[#005EB8] bg-white"}`} data-testid={`${testid}-image`}>
          <input type="file" accept="image/*" className="hidden" disabled={imgBusy} onChange={addImage} />
          <ImagePlus className="h-4 w-4" />
        </label>
        <span className="w-px h-6 bg-slate-200 mx-1" />
        <ToolBtn title="Undo" testid={`${testid}-undo`} disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}><Undo2 className="h-4 w-4" /></ToolBtn>
        <ToolBtn title="Redo" testid={`${testid}-redo`} disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}><Redo2 className="h-4 w-4" /></ToolBtn>
      </div>
      <div className="rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm focus-within:ring-2 focus-within:ring-[#005EB8] focus-within:border-[#005EB8]">
        <EditorContent editor={editor} data-testid={`${testid}-area`} />
      </div>
      <p className="text-[11px] text-slate-400 mt-1.5">Select text to style it — bold, headings, lists, quotes, links and images all work.</p>
    </div>
  );
}
